import React, { useMemo, useState, useEffect } from 'react';
import { Clock, Play, Target, PenLine, Lightbulb, FileText, Zap, ArrowRight, CheckCircle2, Settings, TrendingUp } from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';
import SprintAPIService from '../../services/SprintAPIService';
import WorkloadCalculatorModal from '../Timeline/WorkloadCalculatorModal';
import { toast } from 'react-hot-toast';

/**
 * Filter timeline items to only include actionable tasks (not reminders or events)
 * Excludes: deadline_reminders, reminders, events, and items without workflow_key
 */
const getActionableTasks = (timelineItems) => {
  if (!timelineItems || !Array.isArray(timelineItems)) return [];

  return timelineItems.filter(item => {
    if (item.source_type === 'deadline_reminder') return false;
    if (['reminder', 'event', 'college_deadline', 'deadline'].includes(item.source_type)) return false;
    if (!item.workflow_key) return false;
    if (item.completion_status === 'completed' ||
        item.completion_status === 'skipped' ||
        item.completed ||
        item.status === 'completed' ||
        item.status === 'skipped') return false;
    return true;
  });
};

/**
 * Get icon for task category
 */
function getIconForCategory(category) {
  const iconMap = {
    'testing': Target,
    'applications': FileText,
    'essays': PenLine,
    'interviews': Target,
    'profile': Target,
    'research': Lightbulb
  };
  return iconMap[category] || Target;
}

/**
 * Get color for task category
 */
function getColorForCategory(category) {
  const colorMap = {
    'testing': 'azure',
    'applications': 'emerald',
    'essays': 'teal',
    'interviews': 'coral',
    'profile': 'magenta',
    'research': 'gold'
  };
  return colorMap[category] || 'slate';
}

/**
 * EnhancedSprintBlockV2
 *
 * Integrates preset sprints from the sprint system with custom sprint creation.
 * Shows next preset sprint if available, otherwise falls back to recommended tasks.
 */
const EnhancedSprintBlockV2 = ({ timelineItems = [], colleges = [] }) => {
  const { currentUser } = useFamilyContext();
  const { startSession } = useSessionContext();
  const [showCustomSprint, setShowCustomSprint] = useState(false);

  // Sprint system state
  const [loadingSprintData, setLoadingSprintData] = useState(true);
  const [nextPresetSprint, setNextPresetSprint] = useState(null);
  const [progress, setProgress] = useState(null);
  const [usePresetSprints, setUsePresetSprints] = useState(false);

  // Load sprint data
  useEffect(() => {
    if (!currentUser?.id) {
      setLoadingSprintData(false);
      return;
    }

    const loadSprintData = async () => {
      try {
        // Try to load today's sprints
        const todaysSprints = await SprintAPIService.getTodaysSprints(currentUser.id);

        if (todaysSprints && todaysSprints.length > 0) {
          // Find next incomplete sprint
          const nextSprint = todaysSprints.find(s =>
            s.status === 'NOT_STARTED' || s.status === 'IN_PROGRESS'
          );

          if (nextSprint) {
            setNextPresetSprint(nextSprint);
            setUsePresetSprints(true);
          }

          // Load progress
          const progressData = await SprintAPIService.getCurrentProgress(currentUser.id);
          setProgress(progressData);
        }
      } catch (error) {
        console.log('[EnhancedSprintBlockV2] No preset sprints available, falling back to custom mode');
        // This is expected if user doesn't have preset sprints
        setUsePresetSprints(false);
      } finally {
        setLoadingSprintData(false);
      }
    };

    loadSprintData();
  }, [currentUser?.id]);

  // Get deadline context to prioritize urgent work
  const deadlineInfo = useMemo(() => {
    return DeadlineIntelligenceService.getCurrentPhase(colleges);
  }, [colleges]);

  // Find recommended tasks (fallback when no preset sprints)
  const sprintTasks = useMemo(() => {
    if (usePresetSprints && nextPresetSprint) {
      // Use tasks from preset sprint
      return nextPresetSprint.sprint_tasks?.map(st => ({
        ...st.timeline_items,
        id: st.id,
        taskId: st.task_id,
        estimated_duration: st.estimated_duration,
        isOptional: st.is_optional
      })) || [];
    }

    // Fallback: Use recommended tasks from timeline
    const actionableTasks = getActionableTasks(timelineItems);

    if (actionableTasks.length === 0) return [];

    // Prioritize by deadline
    if (deadlineInfo?.college) {
      const urgentCollegeTasks = actionableTasks.filter(task => {
        const collegeName = task.college?.name || task.college?.global_colleges?.name;
        return collegeName === deadlineInfo.college;
      });

      if (urgentCollegeTasks.length > 0) {
        return urgentCollegeTasks.slice(0, 3);
      }
    }

    // Otherwise, find tasks from the same workflow
    const firstTask = actionableTasks[0];
    const sameWorkflowTasks = actionableTasks.filter(
      task => task.workflow_key === firstTask.workflow_key
    );

    return sameWorkflowTasks.length >= 3
      ? sameWorkflowTasks.slice(0, 3)
      : actionableTasks.slice(0, 3);
  }, [timelineItems, deadlineInfo, usePresetSprints, nextPresetSprint]);

  // Calculate total estimated time
  const totalMinutes = useMemo(() => {
    if (usePresetSprints && nextPresetSprint) {
      return nextPresetSprint.estimated_duration;
    }

    return sprintTasks.reduce((sum, task) => {
      const duration = task.mvv_selected && task.mvv_time_required
        ? task.mvv_time_required
        : (task.estimated_duration || 30);
      return sum + duration;
    }, 0);
  }, [sprintTasks, usePresetSprints, nextPresetSprint]);

  // Handle starting preset sprint
  const handleStartPresetSprint = async () => {
    if (!nextPresetSprint) return;

    try {
      // Start sprint in backend
      await SprintAPIService.startSprint(currentUser.id, nextPresetSprint.id);

      // Prepare session data
      const sessionData = {
        title: nextPresetSprint.name,
        sprintId: nextPresetSprint.id,
        tasks: sprintTasks,
        estimatedMinutes: totalMinutes,
        batchType: nextPresetSprint.type
      };

      // Launch session tracker
      startSession(sessionData);

      toast.success(`Started: ${nextPresetSprint.name}`);
    } catch (error) {
      console.error('[EnhancedSprintBlockV2] Error starting preset sprint:', error);
      toast.error('Failed to start sprint');
    }
  };

  // Handle starting custom sprint
  const handleStartCustomSprint = () => {
    const sprintData = {
      title: 'Quick Sprint',
      tasks: sprintTasks.map(task => ({
        ...task,
        useMVV: task.mvv_selected
      })),
      estimatedMinutes: totalMinutes,
      batchType: 'QUICK_SPRINT'
    };
    startSession(sprintData);
  };

  const isEmpty = sprintTasks.length === 0;

  if (loadingSprintData) {
    return (
      <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
          <span className="ml-3 text-sm text-stone-600">Loading sprint...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg overflow-hidden">
      {/* Sprint Header */}
      <div className="bg-slate-900 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-gold-400"></div>
          <h2 className="text-lg font-bold text-white">
            {usePresetSprints ? 'Next Sprint' : "Today's Critical Path"}
          </h2>
        </div>
        {!isEmpty && (
          <div className="flex items-center gap-4 text-white text-sm">
            {usePresetSprints && progress && (
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-stone-400">
                  {progress.sprints_completed} / {progress.sprints_total} complete
                </span>
              </span>
            )}
            <span className="flex items-center gap-2">
              <span className="text-stone-400">{sprintTasks.length} tasks</span>
              <span className="text-stone-600">•</span>
              <span className="text-stone-400">{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m</span>
            </span>
          </div>
        )}
      </div>

      {/* Sprint Name (for preset sprints) */}
      {usePresetSprints && nextPresetSprint && (
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-1">
                {nextPresetSprint.name}
              </h3>
              {nextPresetSprint.description && (
                <p className="text-sm text-stone-600">{nextPresetSprint.description}</p>
              )}
            </div>
            {nextPresetSprint.priority === 'CRITICAL' && (
              <span className="px-3 py-1 bg-coral-100 text-coral-700 text-xs font-bold rounded-full">
                Critical
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sprint Tasks */}
      <div className="px-8 pb-6 space-y-4">
        {isEmpty ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto mb-3" />
            <p className="text-slate-900 font-medium mb-1">All caught up!</p>
            <p className="text-sm text-stone-600">No critical tasks for today</p>
          </div>
        ) : (
          sprintTasks.map((task, index) => {
            const Icon = getIconForCategory(task.category);
            const color = getColorForCategory(task.category);
            const duration = task.estimated_duration || 30;
            const durationText = duration >= 60
              ? `${Math.floor(duration / 60)}h ${duration % 60}m`
              : `${duration} min`;

            return (
              <div
                key={task.id}
                className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-all group"
              >
                <div className="flex items-start gap-4">
                  {/* Task number + icon */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div className={`p-2 bg-${color}-100 rounded-lg border border-${color}-300`}>
                      <Icon className={`w-5 h-5 text-${color}-700`} />
                    </div>
                  </div>

                  {/* Task content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-slate-900">
                        {task.friendly_title || task.title}
                      </h3>
                      <span className="text-sm text-slate-600 font-medium flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-4 h-4" />
                        {durationText}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      {task.rationale && (
                        <div className="flex items-start gap-2">
                          <Zap className="w-4 h-4 text-gold-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Why:</span> {task.rationale}
                          </p>
                        </div>
                      )}
                      {task.college?.name && (
                        <div className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-600">
                            {task.college.name}
                          </p>
                        </div>
                      )}
                      {task.notes && (
                        <div className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-600">{task.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sprint Action */}
      {!isEmpty && (
        <div className="px-8 pb-8 space-y-3">
          <div className="flex gap-3">
            <button
              onClick={usePresetSprints ? handleStartPresetSprint : handleStartCustomSprint}
              className="flex-1 bg-slate-900 text-white border-2 border-slate-900 rounded-lg px-6 py-4 font-bold hover:bg-slate-800 transition-all shadow-sm flex items-center justify-center gap-2 text-base"
            >
              <Play className="w-5 h-5" />
              {usePresetSprints ? 'Start Sprint' : 'Start Quick Sprint'}
            </button>
            <button
              onClick={() => setShowCustomSprint(true)}
              className="bg-white text-slate-900 border-2 border-slate-900 rounded-lg px-6 py-4 font-bold hover:bg-stone-50 transition-all shadow-sm flex items-center justify-center gap-2 text-base"
              title="Customize sprint tasks"
            >
              <Settings className="w-5 h-5" />
              Customize
            </button>
          </div>

          {/* Context hint */}
          {deadlineInfo?.college && !usePresetSprints && (
            <div className="text-xs text-stone-600 text-center">
              📌 Prioritizing {deadlineInfo.college} ({deadlineInfo.daysUntil} days until deadline)
            </div>
          )}

          {/* Preset sprint mode indicator */}
          {usePresetSprints && progress && (
            <div className="text-xs text-stone-600 text-center flex items-center justify-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                progress.on_schedule ? 'bg-teal-500' :
                progress.risk_level === 'AT_RISK' ? 'bg-gold-500' :
                'bg-coral-500'
              }`}></div>
              <span>
                {progress.on_schedule ? 'On Track' : `${progress.sprints_behind} sprint${progress.sprints_behind > 1 ? 's' : ''} behind`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Custom Sprint Builder Modal */}
      <WorkloadCalculatorModal
        isOpen={showCustomSprint}
        onClose={() => setShowCustomSprint(false)}
        colleges={colleges}
        tasks={timelineItems}
        deadlineInfo={deadlineInfo}
        onStartSprint={(sprintData) => {
          startSession(sprintData);
          setShowCustomSprint(false);
        }}
      />
    </div>
  );
};

export default EnhancedSprintBlockV2;
